import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
} from "@mui/material";
import { API_URL } from "../../utils/api-config";

const carburantProviders = ["Total", "Shell", "BP", "Esso", "Avia", "Leclerc", "Autre"];
const lavageProviders = ["Elephant Bleu", "Total Wash", "Wash&Go", "IMO", "BP Wash", "Autre"];

const carburantTypes = ["Diesel", "Essence", "GPL", "Electrique", "Tous carburants"];
const lavageTypes = ["Lavage illimité", "Lavage simple", "Lavage premium", "Programme annuel"];

export default function FuelCardForm() {
  const [category, setCategory] = useState<'carburant' | 'lavage'>('carburant');
  const [form, setForm] = useState({
    cardNumber: "",
    provider: "",
    type: "",
    expiryDate: "",
    isConnected: false,
    externalId: "",
  });
  const [customProvider, setCustomProvider] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCategoryChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCategory: 'carburant' | 'lavage' | null,
  ) => {
    if (newCategory !== null) {
      setCategory(newCategory);
      setForm(prev => ({ ...prev, provider: '', type: '' }));
      setCustomProvider("");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const providerValue = form.provider === "Autre" ? customProvider : form.provider;
      const res = await fetch(`${API_URL}/fuel-cards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ ...form, provider: providerValue, category }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Erreur lors de l'ajout de la carte");
      }
      setSuccess("Carte ajoutée avec succès !");
      setForm({
        cardNumber: "",
        provider: "",
        type: "",
        expiryDate: "",
        isConnected: false,
        externalId: "",
      });
      setCustomProvider("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'ajout de la carte");
    } finally {
      setLoading(false);
    }
  };

  const providers = category === 'carburant' ? carburantProviders : lavageProviders;
  const types = category === 'carburant' ? carburantTypes : lavageTypes;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 500, mx: "auto" }}
    >
      <Typography variant="h6" sx={{ mb: 3 }}>
        Ajouter une carte
      </Typography>

      {/* Sélection de catégorie */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Type de carte
        </Typography>
        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={handleCategoryChange}
          fullWidth
        >
          <ToggleButton value="carburant" sx={{ py: 1.5 }}>
            <Box component="img" src="/carburant-icon.png" sx={{ width: 24, height: 24, mr: 1 }} />
            Carburant
          </ToggleButton>
          <ToggleButton value="lavage" sx={{ py: 1.5 }}>
            <Box component="img" src="/lavage-icon.png" sx={{ width: 24, height: 24, mr: 1 }} />
            Lavage
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <TextField
        label="Numéro de carte"
        name="cardNumber"
        value={form.cardNumber}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
      />
      <TextField
        select
        label="Fournisseur"
        name="provider"
        value={form.provider}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
      >
        {providers.map((p) => (
          <MenuItem key={p} value={p}>
            {p}
          </MenuItem>
        ))}
      </TextField>
      {form.provider === "Autre" && (
        <TextField
          label="Nom du fournisseur"
          value={customProvider}
          onChange={(e) => setCustomProvider(e.target.value)}
          fullWidth
          required
          placeholder="Saisir le nom du fournisseur"
          sx={{ mb: 2 }}
        />
      )}
      <TextField
        select
        label={category === 'carburant' ? "Type de carburant" : "Type de forfait"}
        name="type"
        value={form.type}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 2 }}
      >
        {types.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Date d'expiration"
        name="expiryDate"
        type="date"
        value={form.expiryDate}
        onChange={handleChange}
        fullWidth
        required
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color={category === 'carburant' ? 'primary' : 'info'}
        fullWidth
        disabled={loading}
        startIcon={<Box component="img" src={category === 'carburant' ? "/carburant-icon.png" : "/lavage-icon.png"} sx={{ width: 20, height: 20 }} />}
        sx={{ py: 1.5 }}
      >
        {loading ? <CircularProgress size={24} /> : `Ajouter carte ${category}`}
      </Button>
    </Box>
  );
}
